/**
 * Shared todo editor controls, used by both the quick-add bar and the edit
 * sheet so the two set a schedule, a repeat and an assignee the same way. Kept in
 * the module (no cross-module import — the independence rule).
 */
import { useState } from 'react';
import { DateField, TimeField } from '../platform/DateField';
import type { TodoRecurrence } from '@familyhub/shared';
import { quickDate, WEEKDAYS } from './datetime';

/** A date/time picker: quick chips for the common days, plus an exact fallback. */
export function Scheduler({
  date,
  time,
  onChange,
}: {
  date: string;
  time: string;
  onChange: (date: string, time: string) => void;
}) {
  const quick: { label: string; kind: 'today' | 'tomorrow' | 'weekend' | 'nextWeek' }[] = [
    { label: 'Vandaag', kind: 'today' },
    { label: 'Morgen', kind: 'tomorrow' },
    { label: 'Dit weekend', kind: 'weekend' },
    { label: 'Volgende week', kind: 'nextWeek' },
  ];
  return (
    <div className="todo-sched">
      <div className="todo-sched-quick">
        {quick.map(({ label, kind }) => (
          <button
            key={kind}
            type="button"
            className="chip"
            aria-pressed={!!date && date === quickDate(kind)}
            onClick={() => onChange(quickDate(kind), time)}
          >
            {label}
          </button>
        ))}
        {date && (
          <button type="button" className="chip todo-sched-clear" onClick={() => onChange('', '')}>
            Geen datum
          </button>
        )}
      </div>
      <div className="todo-sched-exact">
        <DateField value={date} onChange={(d) => onChange(d, time)} ariaLabel="Datum" />
        <TimeField value={time} onChange={(t) => onChange(date, t)} ariaLabel="Tijd" disabled={!date} />
      </div>
    </div>
  );
}

/** The state a recurrence editor needs — one hook, shared by add and edit. */
export function useRecurrence(initial: TodoRecurrence | null) {
  const [repeat, setRepeat] = useState<'none' | 'daily' | 'weekly'>(initial?.freq ?? 'none');
  const [interval, setInterval] = useState(initial?.freq === 'daily' ? initial.interval : 1);
  const [weekdays, setWeekdays] = useState<number[]>(initial?.freq === 'weekly' ? initial.weekdays : []);
  const toggleWeekday = (d: number) =>
    setWeekdays((ws) => (ws.includes(d) ? ws.filter((w) => w !== d) : [...ws, d]));
  const reset = () => {
    setRepeat('none');
    setInterval(1);
    setWeekdays([]);
  };
  const recurrence: TodoRecurrence | null =
    repeat === 'daily'
      ? { freq: 'daily', interval: Math.max(1, interval), weekdays: [] }
      : repeat === 'weekly'
        ? { freq: 'weekly', interval: 1, weekdays }
        : null;
  const repeatValid = repeat !== 'weekly' || weekdays.length > 0;
  return { repeat, setRepeat, interval, setInterval, weekdays, toggleWeekday, reset, recurrence, repeatValid };
}

type RecurrenceState = ReturnType<typeof useRecurrence>;

/** The "Herhalen" control: none / every-N-days / on-weekdays, with its sub-field. */
export function RecurrenceEditor({ rec }: { rec: RecurrenceState }) {
  return (
    <>
      <label className="sheet-field">
        <span className="sheet-label">Herhalen</span>
        <select value={rec.repeat} onChange={(e) => rec.setRepeat(e.target.value as 'none' | 'daily' | 'weekly')}>
          <option value="none">Niet</option>
          <option value="daily">Elke zoveel dagen</option>
          <option value="weekly">Op weekdagen</option>
        </select>
      </label>

      {rec.repeat === 'daily' && (
        <label className="sheet-field">
          <span className="sheet-label">Om de</span>
          <div className="sheet-row todo-when">
            <input
              type="number"
              min={1}
              max={365}
              value={rec.interval}
              onChange={(e) => rec.setInterval(Math.max(1, Number(e.target.value) || 1))}
              aria-label="Aantal dagen"
              style={{ width: 72 }}
            />
            <span>{rec.interval === 1 ? 'dag' : 'dagen'}</span>
          </div>
        </label>
      )}

      {rec.repeat === 'weekly' && (
        <div className="sheet-field">
          <span className="sheet-label">Weekdagen</span>
          <div className="sheet-row todo-weekdays">
            {WEEKDAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                className="chip"
                aria-pressed={rec.weekdays.includes(d.value)}
                aria-label={d.short}
                onClick={() => rec.toggleWeekday(d.value)}
              >
                {d.short}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** The assignee picker — only meaningful when the household has more than one member. */
export function AssigneePicker({
  members,
  value,
  onChange,
}: {
  members: { id: string; display_name: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <label className="sheet-field">
      <span className="sheet-label">Wie</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Iedereen</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.display_name}
          </option>
        ))}
      </select>
    </label>
  );
}
